import { meResponse } from '@/features/settings/api/me'
import {
  api,
  ApiError,
  ContractError,
  isSignedIn,
  loadTokens,
  peekTokens,
  subscribeTokens,
  toApiError,
} from '@/shared/api'

import {
  bindChatIdentity,
  fetchChatSession,
  isIdentityTaken,
  retireChatIdentity,
  type ChatSession,
} from '../api/session'

import * as goftino from './goftino'
import {
  CALL_BUDGET_MS,
  retryAfterMs,
  sleep,
  startBudget,
  SupportChatTimeout,
  within,
  type Budget,
} from './timing'

/**
 * The support chat's state and lifecycle, behind `SupportChatProvider`.
 *
 * Everything lives in this closure, in memory: whose chat is loaded and the
 * unread count. Nothing is persisted. The session is fetched with a plain call
 * on every open rather than through a query, because a query would keep the
 * bearer id in the cache, and no body is kept between opens.
 *
 * **Nothing loads until the first `open()`.** Goftino records the pages a
 * visitor views while the widget is loaded, and a user who never asks for
 * help should never give it one. Once loaded, the widget stays up, closed,
 * for the rest of the visit, so an operator's reply can raise the unread dot.
 *
 * **The id is Goftino's, and core binds it.** Goftino accepts only ids its
 * own widget generated. An account with a bound id loads it as `?o=` and
 * applies it with `setUserId`; refused in two fresh loads, the id is retired
 * and a fresh one bound. An account without one loads a fresh widget, waits
 * for Goftino to issue this visitor an id, and binds it with core in the
 * background of an open that has already resolved. First write wins: when
 * another device bound first, the widget reloads under that id.
 *
 * **Which account a widget belongs to.** An unbound widget has no Goftino id
 * to compare, so a loaded widget carries the account — `/me`'s id, the only
 * thing kept from `/me` — and its bound id once known. The account is read
 * with a plain call, not through the cache: a cached `/me`, or one already
 * in flight with the last token, can be the previous account's.
 *
 * **Background work answers to the account.** One account can become another
 * without a sign-out in between, and the check that follows takes a moment.
 * Until it confirms the widget's account for the current tokens, nothing in
 * the background binds, retires or reloads.
 *
 * **The lifecycle is the token store's.** Logout, a `401` anywhere and a
 * failed refresh all end in `clearTokens()`, whose notification arrives
 * before anything navigates. Only a change of identity tears the chat down,
 * and a token change alone is not one:
 *
 *  - signed out, which includes half a login: a user's chat is torn down; a
 *    guest's survives the login steps; with nothing loaded, the ids an
 *    earlier page cached are cleared;
 *  - signed in: a guest's chat is torn down; a user's chat is checked by
 *    fetching the session and the account again, and torn down when the
 *    account or the bound id changed or either fetch failed, so a refresh —
 *    the same account, the same id or still none — keeps it.
 *
 * Another tab is a third source: `goftino.ts` lets the widget go when another
 * tab changes the shared id keys, and the state here follows.
 *
 * **What an operator sees is what the widget connected with.** Goftino sends
 * `setUser`'s argument when its socket connects, not when it is called, so
 * the details and the reference go in with the load, and an open whose
 * details differ from the ones the widget connected with reloads it rather
 * than leave the last operation's reference on a new question.
 */

/**
 * Whose widget is up. A user's carries the account, its bound Goftino id
 * once known, and the id being bound while that is under way.
 */
type Loaded = { kind: 'guest' } | UserWidget

interface UserWidget {
  kind: 'user'
  account: string
  userId: string | null
  binding: string | null
  /** The token epoch this account was last confirmed under. */
  confirmedEpoch: number
  /** Core called the id this widget was issued someone else's. */
  taken: boolean
}

/** How often an open may find the tokens changed under it before giving up. */
const MAX_TRIES = 3

/** A bind that failed on the network, or with a `5xx`, waits this long first. */
const FIRST_BACKOFF_MS = 2_000
const MAX_BACKOFF_MS = 60_000

export interface OpenOptions {
  /** The operation the chat is about, from §16's `prefill.related_reference`. */
  reference?: string
}

/**
 * How an open ended: `opened`; `failed`, with the reason in the state's
 * `error`; or `superseded`, cut short by a sign-out or an account switch,
 * which is nothing to report and nothing to fall back from.
 */
export type OpenOutcome = 'opened' | 'failed' | 'superseded'

export interface SupportChatState {
  opening: boolean
  unread: number
  /**
   * Why the last `open` returned `false`, or why a chat it opened was given
   * up in the background; `null` otherwise.
   */
  error: unknown
  /** Whether the chat's panel is open, which a modal under it must allow for. */
  panelOpen: boolean
}

export interface ChatSettings {
  /** `features.support_chat`, with a widget to load. */
  available: boolean
  /** `/app/config`'s widget id, which a guest loads. */
  guestWidgetId: string | null
}

/** An open the session changed under, abandoned rather than finished. */
export class SupportChatSuperseded extends Error {
  constructor() {
    super('the session changed while the support chat was opening')
    this.name = 'SupportChatSuperseded'
  }
}

/** What core answers while the chat is off, for a caller whose config said it was on. */
function disabledError() {
  return toApiError(404, {
    error: { code: 'not_found', message_fa: '', detail: { reason: 'support_chat_disabled' } },
  })
}

/**
 * The note under a chat opened from an operation. `sendMessage` shows it to
 * the user only. The isolates keep «SX-F-31402» readable inside RTL.
 */
function referenceNote(reference: string): string {
  return `برای پیگیری سریع‌تر، کد پیگیری \u2066${reference}\u2069 را در پیامتان بنویسید.`
}

export function createSupportChat() {
  let settings: ChatSettings = { available: false, guestWidgetId: null }
  let loaded: Loaded | null = null
  /** The `setUser` details the loaded widget connected with, as JSON. */
  let carried: string | null = null
  /** The same details, for a reload the background has to make. */
  let lastUser: goftino.GoftinoUser | null = null
  let state: SupportChatState = { opening: false, unread: 0, error: null, panelOpen: false }
  const listeners = new Set<() => void>()
  /** Set once the provider unmounts: an open still in flight loads nothing. */
  let disposed = false

  /**
   * Opens run one after another, so two quick taps share one load, and the
   * background's reloads queue behind them rather than cut across one.
   */
  let queue: Promise<unknown> = Promise.resolve()
  let inFlight = 0
  /** Bumped by every token change, so an open can tell the session moved. */
  let epoch = 0
  /** Bumped by every token change, so a stale re-check is dropped. */
  let sequence = 0
  let seen: { signedIn: boolean; access: string | null } | null = null

  function emit(next: Partial<SupportChatState>): void {
    state = { ...state, ...next }
    for (const listener of listeners) listener()
  }

  /** Drops what this side knows of a widget that is gone. */
  function forget(): void {
    loaded = null
    carried = null
    lastUser = null
    if (state.unread !== 0 || state.panelOpen) emit({ unread: 0, panelOpen: false })
  }

  function takeDown(): void {
    goftino.teardown()
    forget()
  }

  function opened(): void {
    if (state.unread !== 0 || !state.panelOpen) emit({ unread: 0, panelOpen: true })
  }

  function stillWanted(): void {
    if (disposed) throw new SupportChatSuperseded()
  }

  /** Background work, after whatever open is under way. */
  function enqueue(job: () => Promise<void>): void {
    queue = queue.then(job).catch(() => undefined)
  }

  /**
   * The signed-in account, read with the current token. Not through the
   * query cache: a cached `/me`, or one in flight with the last token, can be
   * the previous account's. Only the id is kept.
   */
  async function fetchAccount(signal: AbortSignal): Promise<string> {
    const me = await api.get('/me', { schema: meResponse, signal })
    return me.id
  }

  /** The session and the account together, under one budget. */
  function fetchIdentity(budget: Budget): Promise<[ChatSession, string]> {
    return Promise.all([
      within(budget, (signal) => fetchChatSession(signal)),
      within(budget, (signal) => fetchAccount(signal)),
    ])
  }

  /** Whether a loaded user's widget is still this account's, under this id. */
  function sameIdentity(mine: Loaded, account: string, userId: string | null): boolean {
    if (mine.kind !== 'user' || mine.account !== account || mine.taken) return false
    if (mine.userId === userId) return true
    // This widget's own bind landed in core before its answer reached here.
    return mine.userId === null && userId !== null && mine.binding === userId
  }

  /* --- loads ----------------------------------------------------------- */

  function userWidget(account: string, userId: string | null, at: number): UserWidget {
    return { kind: 'user', account, userId, binding: null, confirmedEpoch: at, taken: false }
  }

  /**
   * Loads a user's widget, for the account confirmed at token epoch `at`. A
   * token change since then means the account may be another, so nothing
   * loads; one during the load is the caller's to re-check.
   */
  async function loadFor(
    mine: UserWidget,
    widgetId: string,
    user: goftino.GoftinoUser,
    budget: Budget,
    onWidgetId?: (id: string) => void,
  ): Promise<void> {
    if (epoch !== mine.confirmedEpoch) throw new SupportChatSuperseded()
    loaded = mine
    carried = JSON.stringify(user)
    lastUser = user
    try {
      await goftino.load({
        widgetId,
        account: mine.account,
        userId: mine.userId,
        widget: { hasIcon: false },
        user,
        deadline: budget.deadline,
        onWidgetId,
      })
    } catch (error) {
      // Torn down while it loaded: a sign-out, an account switch or another
      // tab, which is not this open failing.
      if (loaded !== mine) throw new SupportChatSuperseded()
      takeDown()
      throw error
    }
    stillWanted()
    if (loaded !== mine) throw new SupportChatSuperseded()
  }

  /**
   * The account's bound id, as `?o=` and then `setUserId`. «Already set» is
   * success: the `?o=` applied it. A refusal reloads once and tries again;
   * refused twice, Goftino no longer knows the id, so it is retired and a
   * fresh one bound. No answer is a network failure, not a refusal: the open
   * fails and nothing is retired. A signed-in user is never left in an
   * anonymous chat that looks identified. The second load has a budget of
   * its own, or a slow network would never reach the retire.
   */
  async function loadBound(
    widgetId: string,
    account: string,
    id: string,
    user: goftino.GoftinoUser,
    budget: Budget,
    at: number,
  ): Promise<void> {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const mine = userWidget(account, id, at)
      const own = attempt === 0 ? null : startBudget()
      try {
        await loadFor(mine, widgetId, user, own ?? budget)
      } finally {
        own?.done()
      }
      let outcome: goftino.SetUserIdOutcome
      try {
        const applying = goftino.setUserId(id)
        goftino.setWidget({ hasIcon: false })
        outcome = await applying
      } catch (error) {
        if (loaded !== mine) throw new SupportChatSuperseded()
        takeDown()
        throw error
      }
      stillWanted()
      if (outcome === 'stale' || loaded !== mine) throw new SupportChatSuperseded()
      if (outcome === 'applied') return
      takeDown()
      if (outcome === 'silent') throw new SupportChatTimeout()
    }

    // Nothing is loaded from here to the fresh load, so a sign-out in
    // between would go unnoticed but for the epoch.
    if (epoch !== at) throw new SupportChatSuperseded()
    const retiring = startBudget()
    try {
      await within(retiring, (signal) => retireChatIdentity(signal))
    } catch (error) {
      if (epoch !== at) throw new SupportChatSuperseded()
      throw error
    } finally {
      retiring.done()
    }
    stillWanted()
    if (epoch !== at) throw new SupportChatSuperseded()
    const fresh = startBudget()
    try {
      await loadUnbound(widgetId, account, user, fresh, false, at)
    } finally {
      fresh.done()
    }
  }

  /**
   * A fresh widget for an account with no id: every cached id cleared, no
   * `?o=` and no `setUserId`. The id Goftino issues is bound in the
   * background; opening never waits for the visitor to type.
   */
  async function loadUnbound(
    widgetId: string,
    account: string,
    user: goftino.GoftinoUser,
    budget: Budget,
    takenBefore: boolean,
    at: number,
  ): Promise<void> {
    const mine = userWidget(account, null, at)
    await loadFor(mine, widgetId, user, budget, (id) => {
      void bindInBackground(id, mine, widgetId, takenBefore)
    })
    try {
      goftino.setWidget({ hasIcon: false })
    } catch (error) {
      if (loaded === mine) takeDown()
      throw error
    }
  }

  /* --- binding --------------------------------------------------------- */

  /**
   * Waits until this widget's account is confirmed for the current tokens —
   * the check every token change starts — or it is gone. `false` when it is
   * gone, or the check never came.
   */
  async function confirmed(mine: UserWidget): Promise<boolean> {
    for (let waited = 0; loaded === mine && mine.confirmedEpoch !== epoch; waited += 250) {
      if (waited > CALL_BUDGET_MS + 1_000) return false
      await sleep(250)
    }
    return loaded === mine
  }

  /**
   * `PUT`s the id the widget was issued, for as long as this widget is the
   * one loaded and its account is confirmed. The chat keeps working
   * throughout.
   *
   *  - the same id back: bound;
   *  - another id: another device bound first, and the widget reloads under
   *    it — whatever was typed here stays in the orphaned conversation;
   *  - `409`: the id is in use, so the widget goes at once and a fresh one
   *    loads, once; a second `409` gives up;
   *  - `429`: waits `retry_after_seconds`;
   *  - the network, a timeout or a `5xx` (a deadlock in core is a `500`):
   *    retries with a backoff;
   *  - `401`: the token flow ends the chat;
   *  - anything else (`404` with the chat switched off, `422`, an answer in
   *    the wrong shape): gives up.
   */
  async function bindInBackground(
    id: string,
    mine: UserWidget,
    widgetId: string,
    takenBefore: boolean,
  ): Promise<void> {
    if (loaded !== mine || mine.userId !== null || mine.binding !== null) return
    mine.binding = id
    let backoff = FIRST_BACKOFF_MS
    try {
      for (;;) {
        if (!(await confirmed(mine))) return
        const budget = startBudget()
        let answer: string
        try {
          answer = await within(budget, (signal) => bindChatIdentity(id, signal))
        } catch (error) {
          if (loaded !== mine) return
          if (isIdentityTaken(error)) {
            taken(mine, widgetId, takenBefore, error)
            return
          }
          const wait = retryAfterMs(error)
          if (wait !== null) {
            await sleep(wait)
            continue
          }
          if (error instanceof ApiError && error.status === 401) return
          if (
            error instanceof ContractError ||
            (error instanceof ApiError && error.status < 500)
          ) {
            giveUp(mine, error)
            return
          }
          await sleep(backoff)
          backoff = Math.min(backoff * 2, MAX_BACKOFF_MS)
          continue
        } finally {
          budget.done()
        }
        if (loaded !== mine) return
        if (answer === id) {
          mine.userId = id
          goftino.markBound(id)
          return
        }
        enqueue(() => switchTo(mine, widgetId, answer))
        return
      }
    } finally {
      mine.binding = null
    }
  }

  function giveUp(mine: Loaded, error: unknown): void {
    if (loaded !== mine) return
    goftino.leaveChatEntry()
    takeDown()
    emit({ error })
  }

  /**
   * The id is in use, possibly by someone else: the widget holding it goes at
   * once, and a fresh one loads after any open under way — once.
   */
  function taken(mine: UserWidget, widgetId: string, takenBefore: boolean, error: unknown): void {
    mine.taken = true
    if (takenBefore) {
      giveUp(mine, error)
      return
    }
    const user = lastUser
    const wasOpen = state.panelOpen
    const at = mine.confirmedEpoch
    goftino.leaveChatEntry()
    takeDown()
    if (!user) return
    enqueue(async () => {
      // Something else loaded since, or the account may have changed.
      if (loaded !== null || epoch !== at) return
      await reload(wasOpen, user, (budget) =>
        loadUnbound(widgetId, mine.account, user, budget, true, at),
      )
    })
  }

  /** Loads under a fresh budget, and reopens the panel if it was open. */
  async function reload(
    wasOpen: boolean,
    user: goftino.GoftinoUser,
    load: (budget: Budget) => Promise<void>,
  ): Promise<void> {
    const budget = startBudget()
    try {
      await load(budget)
      goftino.setUser(user)
      if (wasOpen) {
        goftino.open()
        opened()
      }
    } catch (error) {
      if (!(error instanceof SupportChatSuperseded)) emit({ error })
    } finally {
      budget.done()
    }
  }

  /** Another device bound first: reload bound, under its id. */
  async function switchTo(mine: UserWidget, widgetId: string, id: string): Promise<void> {
    const user = lastUser
    if (!user || !(await confirmed(mine))) return
    const wasOpen = state.panelOpen
    const at = epoch
    goftino.leaveChatEntry()
    takeDown()
    await reload(wasOpen, user, (budget) => loadBound(widgetId, mine.account, id, user, budget, at))
  }

  /* --- opens ----------------------------------------------------------- */

  async function openSignedIn(reference: string | undefined, budget: Budget): Promise<void> {
    for (let tries = 0; tries < MAX_TRIES; tries += 1) {
      if (!isSignedIn(await loadTokens())) throw new SupportChatSuperseded()
      stillWanted()
      const started = epoch

      let next: ChatSession
      let account: string
      try {
        ;[next, account] = await fetchIdentity(budget)
      } catch (error) {
        // A loaded identity that cannot be confirmed does not stay up.
        if (loaded?.kind === 'user') takeDown()
        throw error
      }
      stillWanted()
      // The tokens changed under the fetch: ask again with the new ones.
      if (epoch !== started) continue

      // The reference key goes every time, empty when there is none: whether
      // Goftino merges metadata is undocumented, and an operator must never
      // see the last operation's reference on a new question.
      const user: goftino.GoftinoUser = {
        ...next.profile,
        metadata: [{ key: 'reference', value: reference ?? '' }],
        forceUpdate: true,
      }

      // A guest's, another account's, or bound under another id — rebound
      // elsewhere, or revoked or retired without this device hearing of it —
      // goes before anything is shown. The same widget with other details
      // goes too: only a reconnect sends them.
      if (loaded) {
        if (sameIdentity(loaded, account, next.user_id) && carried === JSON.stringify(user)) {
          if (loaded.kind === 'user') loaded.confirmedEpoch = started
        } else {
          takeDown()
        }
      }

      if (!loaded) {
        if (next.user_id === null) {
          await loadUnbound(next.widget_id, account, user, budget, false, started)
        } else {
          await loadBound(next.widget_id, account, next.user_id, user, budget, started)
        }
      }
      const mine: Loaded | null = loaded
      if (mine === null || mine.kind !== 'user' || mine.account !== account) {
        throw new SupportChatSuperseded()
      }
      // The tokens changed while it loaded: confirm the identity before showing it.
      if (epoch !== started) continue

      try {
        // The documented call. The load already connected with these.
        goftino.setUser(user)
        goftino.open()
        if (reference) goftino.sendMessage({ text: referenceNote(reference) })
      } catch (error) {
        takeDown()
        throw error
      }
      return
    }
    throw new SupportChatSuperseded()
  }

  async function openAsGuest(budget: Budget): Promise<void> {
    if (loaded?.kind === 'user') takeDown()
    if (!loaded) {
      const widgetId = settings.guestWidgetId
      if (!widgetId) throw disabledError()
      const mine: Loaded = { kind: 'guest' }
      loaded = mine
      try {
        await goftino.load({
          widgetId,
          account: null,
          userId: null,
          widget: { hasIcon: false },
          deadline: budget.deadline,
        })
        stillWanted()
        // A login finished while it loaded.
        if (loaded !== mine) throw new SupportChatSuperseded()
        goftino.setWidget({ hasIcon: false })
      } catch (error) {
        if (loaded !== mine) throw new SupportChatSuperseded()
        takeDown()
        throw error
      }
    }
    try {
      goftino.open()
    } catch (error) {
      takeDown()
      throw error
    }
  }

  async function attempt({ reference }: OpenOptions): Promise<void> {
    if (!settings.available) throw disabledError()
    // Never `peekTokens()` here: it is empty until the store has loaded, and
    // `/support` is public, so a cold load would open a signed-in user's
    // chat as a guest's.
    const signedIn = isSignedIn(await loadTokens())
    stillWanted()
    // One budget for the whole open: the session fetch and the load share it.
    const budget = startBudget()
    try {
      if (signedIn) await openSignedIn(reference, budget)
      else await openAsGuest(budget)
    } finally {
      budget.done()
    }
    opened()
  }

  /**
   * Opens the chat, about `reference` when one is given. A failure leaves
   * its reason in the state's `error`, so the caller can show it or fall
   * back; a superseded open leaves `error` empty.
   */
  function open(options: OpenOptions = {}): Promise<OpenOutcome> {
    inFlight += 1
    emit({ opening: true, error: null })
    const run = queue.then(() => attempt(options))
    queue = run.catch(() => undefined)
    const finish = (error: unknown) => {
      inFlight -= 1
      emit({ opening: inFlight > 0, error })
    }
    return run.then(
      () => {
        finish(null)
        return 'opened' as const
      },
      (error: unknown) => {
        if (error instanceof SupportChatSuperseded) {
          finish(null)
          return 'superseded' as const
        }
        finish(error)
        return 'failed' as const
      },
    )
  }

  /* --- the session ----------------------------------------------------- */

  /** The token store's notification. `peekTokens()` holds the new pair by now. */
  function onTokens(): void {
    const pair = peekTokens()
    const now = { signedIn: isSignedIn(pair), access: pair?.accessToken ?? null }
    if (seen && seen.signedIn === now.signedIn && seen.access === now.access) return
    seen = now
    epoch += 1
    const mine = ++sequence

    if (!now.signedIn) {
      if (loaded?.kind === 'guest') return
      if (loaded) {
        takeDown()
      } else {
        // An id cached by an earlier page would outlive this sign-out.
        goftino.clearCachedIds()
      }
      return
    }

    if (!loaded) return
    if (loaded.kind === 'guest') {
      takeDown()
      return
    }
    // Bounded like an open, so a stalled check cannot hold a revoked chat up.
    // The answer is held against whatever user widget is loaded when it
    // comes, which a queued reload may have replaced in the meantime, and
    // confirms it for these tokens: background work waits on that.
    const checkedAt = epoch
    const budget = startBudget()
    fetchIdentity(budget)
      .then(
        ([next, account]) => {
          if (mine !== sequence || loaded?.kind !== 'user') return
          if (sameIdentity(loaded, account, next.user_id)) loaded.confirmedEpoch = checkedAt
          else takeDown()
        },
        () => {
          if (mine === sequence && loaded?.kind === 'user') takeDown()
        },
      )
      .finally(() => budget.done())
  }

  /** Subscribes to the tokens and the widget; the returned stop tears down. */
  function start(): () => void {
    disposed = false
    const stops = [
      subscribeTokens(onTokens),
      goftino.onConflict(forget),
      goftino.onWidgetOpen(opened),
      goftino.onWidgetClose(() => {
        if (state.panelOpen) emit({ panelOpen: false })
      }),
      goftino.onMessage(() => {
        if (loaded !== null && !state.panelOpen) emit({ unread: state.unread + 1 })
      }),
    ]
    void loadTokens().then((pair) => {
      if (!isSignedIn(pair) && loaded === null) goftino.clearCachedIds()
    })
    return () => {
      disposed = true
      for (const stop of stops) stop()
      // Nothing would be left to end it with the session.
      if (loaded !== null) takeDown()
    }
  }

  /**
   * The config's say, read by the next open. A chat already up stays: the
   * config query falls back to everything off when a refetch cannot reach
   * the service, and that must not end a conversation in progress.
   */
  function configure(next: ChatSettings): void {
    settings = next
  }

  return {
    open,
    start,
    configure,
    subscribe(listener: () => void): () => void {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    getSnapshot: (): SupportChatState => state,
  }
}

export type SupportChatController = ReturnType<typeof createSupportChat>
